import React from 'react';
import { Helmet } from 'react-helmet';
import {
  Accordion,
  AccordionContent,
  AccordionPanel,
} from '@americanexpress/dls-react';
import { ExtendReactlytics } from '@americanexpress/reactlytics';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import styles from './CenturionNetworkFAQs.module.scss';

interface FAQ {
  question: string;
  answer: string;
}

interface FAQSection {
  header?: string;
  questions?: FAQ[];
}

// Disable DLS automatic tracking to prevent duplicate or generic FAQ events.
const disableAutomaticTracking = { disable: true };
const faqPanelIdPrefix = 'centurion-network-faq';
const faqTrackingElement = 'Frequently Asked Questions';

export const CenturionNetworkFAQs = () => {
  const { customTrack } = useAnalyticsContext();
  const { centurionNetwork } = useCommonContent() as {
    centurionNetwork?: {
      faqSection?: FAQSection;
    };
  };
  const faqSection = centurionNetwork?.faqSection;
  const { header, questions = [] } = faqSection ?? {};
  const faqQuestionByPanelId = new Map(
    questions.map(({ question }, index) => [
      `${faqPanelIdPrefix}-${index}`,
      question,
    ])
  );

  const handleFaqToggle = (
    event: React.MouseEvent<Element>,
    panelId: string
  ) => {
    const question = faqQuestionByPanelId.get(panelId)!;

    if (event.currentTarget.getAttribute('aria-expanded') !== 'false') return;

    customTrack({
      event: event as React.MouseEvent<HTMLElement>,
      element: faqTrackingElement,
      detail: question,
    });
  };

  if (!header || questions.length === 0) {
    return null;
  }

  const faqPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };

  return (
    <section aria-labelledby="centurion-network-faqs-heading">
      {/* https://developers.google.com/search/docs/appearance/structured-data/faqpage */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqPageJsonLd, null, 2)}
        </script>
      </Helmet>
      <hr className="tw-my-10 tw-border-gray-300" />
      <h2
        id="centurion-network-faqs-heading"
        className="tw-heading-md-regular tw-mb-6 tw-text-gray-800"
      >
        {header}
      </h2>
      <ExtendReactlytics analytics={disableAutomaticTracking}>
        <Accordion
          id="centurion-network-faqs"
          isBordered={false}
          shouldAllowMultipleExpanded={true}
          onAccordionToggle={handleFaqToggle}
          className="tw-bg-gray-100"
        >
          {questions.map(({ question, answer }, index) => {
            const panelId = `${faqPanelIdPrefix}-${index}`;

            return (
              <React.Fragment key={panelId}>
                <AccordionPanel
                  id={panelId}
                  heading={
                    <h3 className="tw-flex tw-min-h-12 tw-items-center">
                      <span className="tw-block important_tw-min-h-0">
                        {question}
                      </span>
                    </h3>
                  }
                />
                <AccordionContent
                  aria-labelledby={panelId}
                  className="important_tw-pb-6 important_tw-pt-0"
                >
                  <div
                    className={`${styles.answer} tw-whitespace-pre-line tw-font-dls tw-text-base tw-text-gray-800 a-children_tw-text-link a-children_tw-underline`}
                    dangerouslySetInnerHTML={{ __html: answer }}
                  />
                </AccordionContent>
              </React.Fragment>
            );
          })}
        </Accordion>
      </ExtendReactlytics>
    </section>
  );
};
